import { H3, PaddedSurface as Surface } from "App/global-style";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CarProductIntroFormValues } from "./interfaces";

import { VEHICLE_TYPE } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleConstants";
import { ContactUsAlert } from "Components/Form/Alert";
import { useTexts } from "Components/TextContext";
import { AlertTitle } from "Components/TextContext/commonComponentEnums";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { useFeatureToggle } from "Store/featureToggles/featureToggleHooks";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { useFindVehicleErrorSelector } from "Store/findVehicle/hooks";
import { ReduxState } from "Store/rootReducer";
import { updateStep } from "Store/steps/stepsSlice";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import isEmpty from "lodash.isempty";
import { CAR_FIND_STEP } from "../../carSteps";
import { VehicleInputType } from "../FormElements/interfaces";
import { FindVehicle } from "./FindVehicle";
import { FindVehicleType } from "./FindVehicleType";
import { findVehicleTextsCar } from "./FindVehicleType/utils";
import { InputTypeButton } from "./InputTypeButton";
import { OwnedVehicles } from "./OwnedVehicles";

interface Props {
  formProps: FormikProps<CarProductIntroFormValues>;
}

export const FormElements = ({ formProps }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = useTexts();

  const findVehicleError = useFindVehicleErrorSelector();
  const { current: vehicle } = useSelector(
    (state: ReduxState) => state.findVehicle,
  );
  const blockingPage = useFeatureToggle(FeatureToggle.BLOCKING_PAGE);

  useEffect(() => {
    if (vehicle) {
      formProps.setFieldValue("year", vehicle.registrationYear);
      formProps.setFieldValue("brand", vehicle.brand);
      formProps.setFieldValue("model", vehicle.model);
      if (vehicle.modelType) {
        formProps.setFieldValue("modelType", vehicle.modelType);
      }
    }
  }, [vehicle]);

  const { vehicleInputType, licensePlateNumber, brand, model, year } =
    formProps.values;

  useEffect(() => {
    let subTitle;

    if (!isEmpty(licensePlateNumber)) {
      if (!isEmpty(brand) && !isEmpty(model)) {
        subTitle = `${brand} ${model} ${licensePlateNumber}`;
      } else {
        subTitle = licensePlateNumber;
      }
    } else if (!isEmpty(brand) && !isEmpty(model) && !isEmpty(year)) {
      subTitle = `${brand} ${model} ${year}`;
    }

    dispatch(
      updateStep({
        stepId: CAR_FIND_STEP.id,
        stepUpdate: {
          subTitle,
        },
      }),
    );
  }, [licensePlateNumber, brand, model, year]);

  return (
    <>
      <OwnedVehicles formProps={formProps}>
        {formProps.values.vehicleInputType === VehicleInputType.OWNED && (
          <ThemedButton
            as="button"
            onClick={() =>
              formProps.setFieldValue(
                "vehicleInputType",
                VehicleInputType.LICENSE_PLATE,
              )
            }
            data-trackid={buttonTrackId("vehicle-input-type")}
            trackId={buttonTrackId("vehicle-input-type")}
          >
            {getText("carform.button.searchforyourcar")}
          </ThemedButton>
        )}
      </OwnedVehicles>

      {vehicleInputType !== VehicleInputType.OWNED && (
        <Surface>
          <H3>{getText("carform.subheader.searchforcar")}</H3>
          {(vehicleInputType === VehicleInputType.MANUAL ||
            vehicleInputType === VehicleInputType.LICENSE_PLATE ||
            !isDefined(vehicleInputType)) && (
            <FindVehicle formProps={formProps} />
          )}

          {vehicleInputType === VehicleInputType.MANUAL && (
            <FindVehicleType
              vehicleType={VEHICLE_TYPE.CAR}
              vehicleFetched={!!vehicle}
              formProps={formProps}
              texts={findVehicleTextsCar}
            />
          )}

          {!isDefined(findVehicleError) && <InputTypeButton />}
        </Surface>
      )}

      {formProps.values.isAlreadyInsured !== true &&
        formProps.values.canBeBoughtOnline === false &&
        !blockingPage && (
          <ContactUsAlert
            title={getText(AlertTitle.CALL_US)}
            description={getText(
              "carform.alertdescription.brand-cant_buy_online",
            )}
            alertId="vehicleCantBeBoughtOnline"
            variant="border"
            marginBottom="32px"
          />
        )}
    </>
  );
};
